import React, { useState, useEffect } from 'react';
import { useDataProvider, useNotify, Title, useTranslate } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Stack,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  ExpandMore,
  Search,
  Refresh,
  FileDownload,
  Router,
  SwapVert,
  Send,
  CallReceived,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle,
} from '@mui/icons-material';
import { format, subHours, startOfDay } from 'date-fns';
import { useSearchParams, useNavigate } from 'react-router-dom';

// Log level icon mapping
const LogLevelIcon = ({ level }: { level: string }) => {
  switch (level?.toLowerCase()) {
    case 'error':
    case 'fatal':
      return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
    case 'warn':
    case 'warning':
      return <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
    case 'info':
      return <InfoIcon sx={{ color: 'info.main', fontSize: 20 }} />;
    case 'debug':
      return <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />;
    default:
      return <InfoIcon sx={{ color: 'grey.500', fontSize: 20 }} />;
  }
};

// Direction badge
const DirectionBadge = ({ direction }: { direction?: string }) => {
  const translate = useTranslate();
  if (!direction) return null;
  const isOutgoing = direction === 'outgoing' || direction === 'sent';
  return (
    <Chip
      label={isOutgoing ? translate('ocppLogs.badges.outgoing') : translate('ocppLogs.badges.incoming')}
      size="small"
      variant="outlined"
      icon={isOutgoing ? <Send /> : <CallReceived />}
      color={isOutgoing ? 'success' : 'info'}
    />
  );
};

// OCPP message badge
const OcppMessageBadge = ({ action }: { action?: string }) => {
  if (!action) return null;
  const colors: Record<string, any> = {
    'StartTransaction': 'success',
    'StopTransaction': 'error',
    'MeterValues': 'primary',
    'StatusNotification': 'info',
    'Authorize': 'warning',
    'Heartbeat': 'default',
  };
  return (
    <Chip
      label={action}
      color={colors[action] || 'default'}
      size="small"
      icon={<SwapVert />}
      sx={{ mr: 1 }}
    />
  );
};

// Single log entry
const LogEntry = ({ log, index }: { log: any; index: number }) => {
  const translate = useTranslate();
  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 1, overflow: 'hidden' }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          backgroundColor: expanded ? 'action.hover' : 'background.paper',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: { xs: 1, sm: 2 }, overflow: 'hidden', minWidth: 0 }}>
          <LogLevelIcon level={log.level} />
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {log.timestamp ? format(new Date(log.timestamp), 'MM/dd HH:mm:ss.SSS') : '-'}
              </Typography>
              <OcppMessageBadge action={log.ocpp_action} />
              <DirectionBadge direction={log.ocpp_direction} />
            </Box>
            <Typography variant="body2" noWrap>
              {log.message}
            </Typography>
          </Box>
          {log.error_message && <ErrorIcon sx={{ color: 'error.main', fontSize: 20, flexShrink: 0 }} />}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ backgroundColor: 'grey.50', p: { xs: 1.5, sm: 2 }, overflow: 'hidden' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', width: { xs: '35%', sm: '25%' }, minWidth: 0, verticalAlign: 'top' }}>{translate('ocppLogs.logEntry.timestamp')}</TableCell>
              <TableCell sx={{ wordBreak: 'break-word' }}>{log.timestamp ? format(new Date(log.timestamp), 'PPpp') : '-'}</TableCell>
            </TableRow>
            {log.ocpp_action && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'top' }}>{translate('ocppLogs.logEntry.ocppAction')}</TableCell>
                <TableCell>{log.ocpp_action}</TableCell>
              </TableRow>
            )}
            {log.ocpp_direction && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'top' }}>{translate('ocppLogs.logEntry.direction')}</TableCell>
                <TableCell><DirectionBadge direction={log.ocpp_direction} /></TableCell>
              </TableRow>
            )}
            {log._source && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'top' }}>{translate('ocppLogs.logEntry.fullLogData')}</TableCell>
                <TableCell sx={{ p: { xs: 0.5, sm: 1 } }}>
                  <Box component="pre" sx={{ p: 1, backgroundColor: 'grey.100', borderRadius: 1, overflow: 'auto', maxHeight: 400, fontSize: '0.75rem', m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(log._source, null, 2)}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
};

export const OcppLogsQueryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();

  // OCPP Servers
  const [servers, setServers] = useState<any[]>([]);
  const [loadingServers, setLoadingServers] = useState(true);
  const [selectedServer, setSelectedServer] = useState<any | null>(null);

  // Chargers
  const [chargers, setChargers] = useState<any[]>([]);
  const [loadingChargers, setLoadingChargers] = useState(false);
  const [selectedCharger, setSelectedCharger] = useState<any | null>(null);

  // Filters
  const [action, setAction] = useState(searchParams.get('action') || '');
  const [direction, setDirection] = useState(searchParams.get('direction') || '');
  const [logLevel, setLogLevel] = useState(searchParams.get('level') || '');
  const [dateRange, setDateRange] = useState(searchParams.get('range') || 'last_1_hour');
  const [startDate, setStartDate] = useState(searchParams.get('start') || format(subHours(new Date(), 1), "yyyy-MM-dd'T'HH:mm"));
  const [endDate, setEndDate] = useState(searchParams.get('end') || format(new Date(), "yyyy-MM-dd'T'HH:mm"));

  // Data
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [perPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle date range quick select
  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
    const now = new Date();
    switch (range) {
      case 'last_1_hour':
        setStartDate(format(subHours(now, 1), "yyyy-MM-dd'T'HH:mm"));
        setEndDate(format(now, "yyyy-MM-dd'T'HH:mm"));
        break;
      case 'last_24_hours':
        setStartDate(format(subHours(now, 24), "yyyy-MM-dd'T'HH:mm"));
        setEndDate(format(now, "yyyy-MM-dd'T'HH:mm"));
        break;
      case 'today':
        setStartDate(format(startOfDay(now), "yyyy-MM-dd'T'HH:mm"));
        setEndDate(format(now, "yyyy-MM-dd'T'HH:mm"));
        break;
    }
  };

  // Fetch logs
  const fetchLogs = async () => {
    if (!selectedServer) {
      notify('Please select an OCPP server first', { type: 'warning' });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build query params
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('per_page', perPage.toString());
      params.set('start_time', new Date(startDate).toISOString());
      params.set('end_time', new Date(endDate).toISOString());
      params.set('server', selectedServer.id); // Add server ID to params

      if (selectedCharger) params.set('charger_name', selectedCharger.name);
      if (action) params.set('action', action);
      if (direction) params.set('direction', direction);
      if (logLevel) params.set('level', logLevel);

      // Update URL with current filters
      setSearchParams(params);

      // Call backend API
      const response = await dataProvider.getList('ocpp-logs', {
        pagination: { page, perPage },
        sort: { field: 'timestamp', order: 'ASC' },
        filter: Object.fromEntries(params),
      });

      setLogs(response.data || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      console.error('Failed to fetch OCPP logs:', err);
      setError(err.message || 'Failed to fetch OCPP logs');
      notify('Failed to fetch OCPP logs', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch OCPP servers on mount
  useEffect(() => {
    const loadServers = async () => {
      try {
        setLoadingServers(true);
        const response = await dataProvider.getList('ocpp-servers', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: { status: 'active' },
        });
        setServers(response.data || []);

        // If there's a server param in URL, find and set it
        const serverParam = searchParams.get('server');
        if (serverParam && response.data) {
          const server = response.data.find((s: any) => s.id === serverParam);
          if (server) {
            setSelectedServer(server);
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch OCPP servers:', err);
        notify('Failed to fetch OCPP servers', { type: 'error' });
      } finally {
        setLoadingServers(false);
      }
    };
    loadServers();
  }, []);

  // Fetch chargers when OCPP server changes
  useEffect(() => {
    if (!selectedServer) {
      setChargers([]);
      setSelectedCharger(null);
      return;
    }

    const loadChargers = async () => {
      try {
        setLoadingChargers(true);
        const response = await dataProvider.getList(`ocpp-servers/${selectedServer.id}/chargers`, {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        });
        setChargers(response.data || []);

        // If there's a charger param in URL, find and set it
        const chargerParam = searchParams.get('charger');
        if (chargerParam && response.data) {
          const charger = response.data.find((c: any) => c.name === chargerParam);
          if (charger) {
            setSelectedCharger(charger);
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch chargers:', err);
        notify('Failed to fetch chargers for selected server', { type: 'error' });
      } finally {
        setLoadingChargers(false);
      }
    };
    loadChargers();
  }, [selectedServer]);

  // Auto-fetch on mount if params exist
  useEffect(() => {
    if (searchParams.get('charger') && searchParams.get('server')) {
      fetchLogs();
    }
  }, []);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%' }}>
      <Title title={translate('ocppLogs.title')} />

      <Box sx={{ mb: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
        <Router sx={{ fontSize: { xs: 28, sm: 40 }, color: 'primary.main' }} />
        <Box>
          <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>{translate('ocppLogs.pageTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('ocppLogs.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Filters Card */}
      <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }} gutterBottom>
            {translate('ocppLogs.filters.title')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Row 1: OCPP Server Selection (Required) */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  options={servers}
                  loading={loadingServers}
                  value={selectedServer}
                  onChange={(event, newValue) => {
                    setSelectedServer(newValue);
                  }}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return `${option.name}${option.status !== 'active' ? ` (${option.status})` : ''}`;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('ocppLogs.filters.ocppServerRequired')}
                      required
                      placeholder={loadingServers ? translate('ocppLogs.messages.loadingServers') : translate('ocppLogs.filters.ocppServerPlaceholder')}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingServers ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Typography variant="body1">{option.name}</Typography>
                        {option.status !== 'active' && (
                          <Typography variant="caption" color="text.secondary">
                            Status: {option.status}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText={loadingServers ? translate('ocppLogs.messages.loading') : translate('ocppLogs.messages.noServersFound')}
                  disabled={loadingServers}
                />
              </Box>
              {selectedServer && (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <Alert severity="info" sx={{ width: '100%' }}>
                    {translate('ocppLogs.messages.serverSelected')}
                  </Alert>
                </Box>
              )}
            </Box>

            {/* Row 2: Charger Name and Date Range */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  options={chargers}
                  loading={loadingChargers}
                  value={selectedCharger}
                  onChange={(event, newValue) => {
                    setSelectedCharger(newValue);
                  }}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.name || '';
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('ocppLogs.filters.charger')}
                      placeholder={loadingChargers ? translate('ocppLogs.messages.loadingChargers') : translate('ocppLogs.filters.chargerPlaceholder')}
                      helperText={translate('ocppLogs.filters.chargerHelper')}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingChargers ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Typography variant="body1">{option.name}</Typography>
                        {option.site_id && (
                          <Typography variant="caption" color="text.secondary">
                            Site ID: {option.site_id}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText={loadingChargers ? translate('ocppLogs.messages.loading') : selectedServer ? translate('ocppLogs.messages.noChargersFound') : translate('ocppLogs.messages.selectServerFirst')}
                  disabled={!selectedServer || loadingChargers}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth disabled={!selectedServer}>
                  <InputLabel>{translate('ocppLogs.filters.dateRange')}</InputLabel>
                  <Select
                    value={dateRange}
                    label={translate('ocppLogs.filters.dateRange')}
                    onChange={(e) => handleDateRangeChange(e.target.value)}
                  >
                    <MenuItem value="last_1_hour">{translate('ocppLogs.dateRanges.last1Hour')}</MenuItem>
                    <MenuItem value="last_24_hours">{translate('ocppLogs.dateRanges.last24Hours')}</MenuItem>
                    <MenuItem value="today">{translate('ocppLogs.dateRanges.today')}</MenuItem>
                    <MenuItem value="custom">{translate('ocppLogs.dateRanges.customRange')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Row 3: Start and End Date */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label={translate('ocppLogs.filters.startDateTime')}
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setDateRange('custom');
                  }}
                  InputLabelProps={{ shrink: true }}
                  disabled={!selectedServer}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label={translate('ocppLogs.filters.endDateTime')}
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setDateRange('custom');
                  }}
                  InputLabelProps={{ shrink: true }}
                  disabled={!selectedServer}
                />
              </Box>
            </Box>

            {/* Row 4: Action, Direction, Log Level */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth disabled={!selectedServer}>
                  <InputLabel>{translate('ocppLogs.filters.actionType')}</InputLabel>
                  <Select
                    value={action}
                    label={translate('ocppLogs.filters.actionType')}
                    onChange={(e) => setAction(e.target.value)}
                  >
                    <MenuItem value="">{translate('ocppLogs.actions.allActions')}</MenuItem>
                    <MenuItem value="StartTransaction">{translate('ocppLogs.actions.startTransaction')}</MenuItem>
                    <MenuItem value="StopTransaction">{translate('ocppLogs.actions.stopTransaction')}</MenuItem>
                    <MenuItem value="MeterValues">{translate('ocppLogs.actions.meterValues')}</MenuItem>
                    <MenuItem value="StatusNotification">{translate('ocppLogs.actions.statusNotification')}</MenuItem>
                    <MenuItem value="Authorize">{translate('ocppLogs.actions.authorize')}</MenuItem>
                    <MenuItem value="Heartbeat">{translate('ocppLogs.actions.heartbeat')}</MenuItem>
                    <MenuItem value="BootNotification">{translate('ocppLogs.actions.bootNotification')}</MenuItem>
                    <MenuItem value="RemoteStartTransaction">{translate('ocppLogs.actions.remoteStartTransaction')}</MenuItem>
                    <MenuItem value="RemoteStopTransaction">{translate('ocppLogs.actions.remoteStopTransaction')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth disabled={!selectedServer}>
                  <InputLabel>{translate('ocppLogs.filters.direction')}</InputLabel>
                  <Select
                    value={direction}
                    label={translate('ocppLogs.filters.direction')}
                    onChange={(e) => setDirection(e.target.value)}
                  >
                    <MenuItem value="">{translate('ocppLogs.directions.allDirections')}</MenuItem>
                    <MenuItem value="incoming">{translate('ocppLogs.directions.incoming')}</MenuItem>
                    <MenuItem value="outgoing">{translate('ocppLogs.directions.outgoing')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth disabled={!selectedServer}>
                  <InputLabel>{translate('ocppLogs.filters.logLevel')}</InputLabel>
                  <Select
                    value={logLevel}
                    label={translate('ocppLogs.filters.logLevel')}
                    onChange={(e) => setLogLevel(e.target.value)}
                  >
                    <MenuItem value="">{translate('ocppLogs.levels.allLevels')}</MenuItem>
                    <MenuItem value="error">{translate('ocppLogs.levels.error')}</MenuItem>
                    <MenuItem value="warn">{translate('ocppLogs.levels.warning')}</MenuItem>
                    <MenuItem value="info">{translate('ocppLogs.levels.info')}</MenuItem>
                    <MenuItem value="debug">{translate('ocppLogs.levels.debug')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ mt: { xs: 2, sm: 3 }, display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Search />}
              onClick={fetchLogs}
              disabled={loading || !selectedServer}
            >
              {translate('ocppLogs.buttons.searchLogs')}
            </Button>
            <Tooltip title={translate('ocppLogs.buttons.refresh')}>
              <IconButton onClick={fetchLogs} disabled={loading || !selectedServer}>
                <Refresh />
              </IconButton>
            </Tooltip>
            {!selectedServer && (
              <Alert severity="warning" sx={{ flex: 1 }}>
                {translate('ocppLogs.messages.selectServer')}
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Results Card */}
      <Card sx={{ overflow: 'hidden', maxWidth: '100%' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: { xs: 1.5, sm: 2 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {translate('ocppLogs.results.title')}
            </Typography>
            {logs.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {translate('ocppLogs.results.showing', { count: logs.length, total })}
              </Typography>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : logs.length === 0 ? (
            <Alert severity="info">
              {translate('ocppLogs.messages.noLogsFound')}
            </Alert>
          ) : (
            <>
              {logs.map((log, index) => (
                <LogEntry key={log.id || index} log={log} index={index} />
              ))}

              {total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 3 } }}>
                  <Stack spacing={2}>
                    <Pagination
                      count={Math.ceil(total / perPage)}
                      page={page}
                      onChange={(e, value) => {
                        setPage(value);
                        fetchLogs();
                      }}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Stack>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OcppLogsQueryPage;
